import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, Globe, Info, Shield, Layers, BarChart3, Settings as SettingsIcon, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import { shopService } from '../../services/shopService';
import { showSuccess, showError } from '../../utils/alert';

const Settings = () => {
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gmbLink, setGmbLink] = useState('');

  useEffect(() => {
    const fetchShop = async () => {
      try {
        const currentShop = await authService.getCurrentShop();
        if (currentShop) {
          setShop(currentShop);
          setGmbLink(currentShop.gmbLink || '');
        }
      } catch (error) {
        console.error("Error fetching shop:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchShop();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await shopService.updateShop(shop.id, { gmbLink });
      showSuccess('Success', 'Shop settings updated successfully!');
    } catch (error) {
      showError('Error', 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    navigate('/login');
  };

  if (loading) {
    return <div className="min-h-screen bg-[#fdfdfd] flex items-center justify-center text-black font-serif italic tracking-widest">Accessing Settings Node...</div>;
  }

  return (
    <div className="min-h-screen bg-[#fdfdfd] flex text-[#1a1a1a] relative">
      <div className="absolute inset-0 luxury-grid-bg pointer-events-none" />

      {/* Mini Sidebar */}
      <aside className="w-24 bg-white border-r border-black/[0.05] flex flex-col items-center py-10 gap-10 shrink-0 relative z-10">
        <div className="w-12 h-12 bg-black rounded-2xl flex items-center justify-center text-[#d4af37] shadow-xl">
          <Layers size={24} />
        </div>
        
        <nav className="flex flex-col gap-6">
          <SidebarIcon icon={<BarChart3 size={20} />} onClick={() => navigate('/admin/dashboard')} />
          <SidebarIcon icon={<SettingsIcon size={20} />} active />
          <SidebarIcon icon={<Shield size={20} />} />
        </nav>

        <div className="mt-auto">
          <button 
            onClick={handleLogout}
            className="w-12 h-12 rounded-2xl bg-rose-500/5 text-rose-500 flex items-center justify-center hover:bg-rose-500 hover:text-white transition-all"
          >
            <LogOut size={20} />
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 h-screen overflow-y-auto custom-scrollbar p-12 relative z-10">
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/admin/dashboard')}
          className="flex items-center gap-2 text-[#999] hover:text-black transition-colors text-[10px] font-bold uppercase tracking-widest mb-12"
        >
          <ArrowLeft size={16} />
          Return to Dashboard
        </motion.button>

        <div className="max-w-4xl">
          <header className="mb-12">
            <h1 className="text-4xl font-serif text-black tracking-tight mb-2">Shop Settings</h1>
            <p className="text-[#666] text-[10px] font-bold uppercase tracking-[0.3em]">Configure your digital presence and node parameters</p>
          </header>

          <div className="luxury-card p-12">
            <form onSubmit={handleSave} className="space-y-12">
              <div className="space-y-8">
                <div className="flex items-center gap-4 pb-6 border-b border-black/5">
                  <Globe className="text-[#d4af37]" size={20} />
                  <h3 className="text-sm font-bold uppercase tracking-widest text-black">External Integrations</h3>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-[10px] font-bold text-[#999] uppercase tracking-widest ml-1">Google My Business (GMB) Review Link</label>
                    <div className="group relative">
                      <Info size={14} className="text-[#ccc] cursor-help" />
                      <div className="absolute bottom-full right-0 mb-2 w-64 p-3 bg-black text-white text-[9px] rounded-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 leading-relaxed">
                        This link will be attached to the "Share Privilege" button on the customer's success page. Use your GMB review short-link here.
                      </div>
                    </div>
                  </div>
                  
                  <div className="relative">
                    <div className="absolute inset-y-0 left-6 flex items-center pointer-events-none">
                      <Globe size={18} className="text-[#ccc]" />
                    </div>
                    <input
                      type="url"
                      value={gmbLink}
                      onChange={(e) => setGmbLink(e.target.value)}
                      placeholder="https://g.page/r/your-shop-id/review"
                      className="w-full bg-black/[0.02] border border-black/[0.05] rounded-2xl py-5 pl-16 pr-6 text-sm text-black focus:outline-none focus:border-[#d4af37]/50 transition-all font-medium"
                    />
                  </div>
                  <p className="text-[9px] text-[#bbb] italic">Tip: Use your direct Google Review link to encourage customer feedback after they claim a reward.</p>
                </div>
              </div>

              <div className="pt-6">
                <button
                  type="submit"
                  disabled={saving}
                  className="luxury-button w-full sm:w-auto flex items-center justify-center gap-3 !px-12 !py-5 shadow-2xl"
                >
                  {saving ? 'Synchronizing...' : <><Save size={18} /> Deploy Changes</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

const SidebarIcon = ({ icon, active, onClick }) => (
  <button 
    onClick={onClick}
    className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-300 ${
      active ? 'bg-black text-[#d4af37] shadow-xl' : 'text-[#ccc] hover:text-black hover:bg-black/5'
    }`}
  >
    {icon}
  </button>
);

export default Settings;
